import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase, Sorting, SortDirection } from 'app/shared/lib/essette-site';
import { CaseService } from 'app/cm/services/case-service';
import { bindable } from 'aurelia-typed-observable-plugin';
import { apiCall } from 'app/shared/lib/essette-api';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { openMemberSummary } from 'app/cd/components/summary/member/member-summary';
import { openCaseSummary } from 'app/cm/components/summary/case-summary';
import * as CM from 'app/cm/models/essette-api';

@autoinject()
export class CurrentCaseRoutingGrid extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.stringNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    protected householdCaseId: string | null = null;

    protected cases: ReadonlyArray<CM.Case> = [];

    protected showMemberSummaryLink: boolean = false;
    protected openMemberSummary = openMemberSummary;
    protected showCaseSummaryLink: boolean = false;
    protected hasCaseRoute = false;
    protected openCaseSummary = openCaseSummary;
    private sorting: Sorting = new Sorting();
    protected resultCount: number = 0;
    protected pageSize: number = 25;

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: CaseService,
        ...rest: any[]) {
        super(...rest);
        this.checkPermissions();

        // Default Sort
        this.sorting.entries = [
            {
                reference: 'member.description',
                direction: SortDirection.Ascending
            }
        ];
    }

    protected bind(): void {
        this.reload();
    }


    ////****************** Events ***************************************************////
    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
            if(this.sorting.entries.length === 0) {
                this.sorting.entries = [
                    {
                        reference: 'member.description',
                        direction: SortDirection.Ascending
                    }
                ];
            }
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.reload();
    }


    ////****************** Functions ************************************************////
    public async reload(): Promise<void> {
        await this.loadCasesWithQueues();
    }

    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.hasCaseRoute = this.accountContext.hasRouteAccess('cm.case');
        this.showMemberSummaryLink = this.accountContext.hasRouteAccess('cd.member.summary');
        this.showCaseSummaryLink = this.accountContext.hasRouteAccess('cm.case.summary');
    }


    ////****************** Service Calls ********************************************////
    @apiCall
    public async loadCasesWithQueues(): Promise<void> {
        if(!this.householdCaseId) return;

        const options: Partial<IQueryLoadOptions> = {
            includeInactive: false,
            sorting: this.sorting,
            page: this.$pager?.page || 0,
            top: this.pageSize
        };

        const includes: string[] = [
            'member',
            'assignedTo',
            'queues'
        ];

        try {
            this.state.beginLoad();
            const response = await this.service.loadCasesByHouseholdCase(this.householdCaseId, options, includes);

            this.cases = response.results;
            this.resultCount = response.count || 0;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }
}
